import React, { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Eyebrow, Italic } from "./_primitives";
import { ArrowRight, Info } from "lucide-react";

// Color tokens (per spec)
const DEEP = "#124725";
const INK_CARD = "#0c2e18";
const AMBER = "#e8a317";
const LIME = "#aece4f";
const PAPER = "#f7f8f3";
const MUTED = "#5d6b60";
const INK = "#14201a";

const MODELS = {
  studio: {
    key: "studio",
    label: "Venture Studio",
    unit: "lakh",
    min: 5,
    max: 50,
    step: 1,
    default: 10,
    horizon: "Held ~4 years",
    note: "Concentrated. Hands-on co-build. Higher upside.",
    base: { multiple: 2.5, label: "2.5× · ~26% a year" },
    strong: { multiple: 3.8, label: "3.8× · ~40% a year — matches our average realised MOIC" },
  },
  fund: {
    key: "fund",
    label: "VC Fund",
    unit: "crore",
    min: 1,
    max: 10,
    step: 0.5,
    default: 1,
    horizon: "Held ~5 years",
    note: "Diversified across 25+ startups. Lower risk, smoother returns.",
    base: { multiple: 2.5, label: "2.5× · ~20% a year, net of fees" },
    strong: { multiple: 3.0, label: "3.0× · ~25% a year, net of fees" },
  },
};

// Indian number formatter
// inputUnit = "lakh" or "crore"; value is in that unit
function formatINR(value, inputUnit, opts = {}) {
  const { allowCrossover = true } = opts;
  // Convert to lakh internally
  let lakhs = inputUnit === "crore" ? value * 100 : value;
  // If crossing 100 lakh and crossover allowed, display in crore
  let displayUnit = inputUnit;
  let displayVal = value;
  if (allowCrossover && inputUnit === "lakh" && lakhs >= 100) {
    displayUnit = "crore";
    displayVal = lakhs / 100;
  }
  // Format to one decimal max, drop trailing .0
  let rounded = Math.round(displayVal * 10) / 10;
  const str = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return { num: str, unit: displayUnit };
}

// Animated count-up display
function CountAmount({ value, unit, reduced, color }) {
  const [display, setDisplay] = useState(value);
  const rafRef = useRef();
  const fromRef = useRef(value);
  const toRef = useRef(value);

  useEffect(() => {
    if (reduced) {
      setDisplay(value);
      return;
    }
    if (value === toRef.current) return;
    fromRef.current = display;
    toRef.current = value;
    const start = performance.now();
    const duration = 380;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const next = fromRef.current + (toRef.current - fromRef.current) * eased;
      setDisplay(next);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value, reduced]);

  const formatted = formatINR(display, unit);
  return (
    <span
      className="font-serif-display font-extrabold leading-none tracking-tight whitespace-nowrap"
      style={{
        color,
        fontSize: "clamp(1.6rem, 3.5vw, 2.6rem)",
      }}
    >
      ₹{formatted.num} {formatted.unit}
    </span>
  );
}

export default function ReturnsLab({ onOpenInvest }) {
  const reduced = useReducedMotion();
  const [modelKey, setModelKey] = useState("studio");
  const model = MODELS[modelKey];
  const [amount, setAmount] = useState(model.default);
  const [expandedHurdle, setExpandedHurdle] = useState(false);

  // Reset amount when switching model
  function switchModel(key) {
    setModelKey(key);
    setAmount(MODELS[key].default);
  }

  const baseValue = amount * model.base.multiple;
  const strongValue = amount * model.strong.multiple;

  // Progress widths: base relative to strong as max scale; strong fixed near max
  const baseW = Math.min(100, (model.base.multiple / model.strong.multiple) * 92);
  const strongW = 92;

  // Compute slider gradient fill % for visual feedback
  const sliderFillPct = ((amount - model.min) / (model.max - model.min)) * 100;

  const youInvest = formatINR(amount, model.unit, { allowCrossover: false });

  return (
    <section
      id="returns-lab"
      data-testid="returns-lab-section"
      className="relative overflow-hidden"
      style={{ background: PAPER }}
    >
      {/* Faint amber accent line top-left */}
      <div
        className="absolute left-6 top-0 bottom-0 w-px pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(232,163,23,0.6), rgba(232,163,23,0) 70%)",
        }}
      />
      {/* Decorative amber dot */}
      <div
        className="absolute pointer-events-none hidden lg:block"
        style={{
          left: "calc(1.5rem - 6px)",
          top: "40%",
          width: 12,
          height: 12,
          borderRadius: "50%",
          background: AMBER,
          boxShadow: "0 0 22px rgba(232,163,23,0.45)",
        }}
      />

      <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        {/* Header */}
        <div className="mb-10 lg:mb-14 max-w-3xl">
          <Eyebrow color={AMBER}>For Investors</Eyebrow>
          <h2
            className="font-serif-display font-extrabold leading-[1.02] tracking-tight"
            style={{
              color: DEEP,
              fontSize: "clamp(2.4rem, 5vw, 3.6rem)",
            }}
            data-testid="returns-lab-title"
          >
            The <Italic className="text-[#14401F]">Returns Lab</Italic>.
          </h2>
          <p
            className="mt-5 text-sm lg:text-base leading-relaxed max-w-xl"
            style={{ color: MUTED, fontFamily: "var(--font-body)" }}
          >
            Slide the amount, pick a model, and see illustrative targets for the
            Indian early-stage market. Built on the same scenarios we share
            with LPs.
          </p>
        </div>

        {/* Main calculator card */}
        <div
          className="rounded-[22px] bg-white p-6 lg:p-9 border"
          style={{ borderColor: "rgba(18,71,37,0.08)" }}
          data-testid="returns-lab-card"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* INPUT PANEL */}
            <div className="flex flex-col">
              {/* Header row: toggle + YOU INVEST label */}
              <div className="flex items-center gap-4 mb-8 flex-wrap">
                <ModelToggle
                  modelKey={modelKey}
                  onChange={switchModel}
                  reduced={reduced}
                />
                <span
                  className="text-[11px] uppercase tracking-[0.22em] font-bold"
                  style={{ color: MUTED, fontFamily: "var(--font-mono)" }}
                >
                  You Invest
                </span>
              </div>

              {/* Amount display */}
              <div className="mb-6">
                <span
                  className="font-serif-display font-extrabold leading-none tracking-tight block"
                  style={{
                    color: DEEP,
                    fontSize: "clamp(2.6rem, 6.5vw, 4.5rem)",
                  }}
                  data-testid="returns-lab-amount"
                >
                  ₹{youInvest.num} {youInvest.unit}
                </span>
              </div>

              {/* Slider */}
              <div className="mb-5">
                <input
                  type="range"
                  min={model.min}
                  max={model.max}
                  step={model.step}
                  value={amount}
                  onChange={(e) => setAmount(parseFloat(e.target.value))}
                  aria-label={`Investment amount in ${model.unit}`}
                  aria-valuetext={`₹${youInvest.num} ${youInvest.unit}`}
                  data-testid="returns-lab-slider"
                  className="rl-slider"
                  style={{
                    "--fill": `${sliderFillPct}%`,
                  }}
                />
              </div>

              {/* Horizon + style note */}
              <p
                className="text-sm"
                style={{ color: MUTED, fontFamily: "var(--font-body)" }}
              >
                {model.horizon} · {model.note}
              </p>
            </div>

            {/* OUTPUT CARD */}
            <motion.div
              key={modelKey}
              initial={reduced ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="rounded-[18px] p-6 lg:p-7 flex flex-col gap-7"
              style={{
                background: INK_CARD,
                color: "#fff",
                boxShadow:
                  "0 18px 40px -20px rgba(12,46,24,0.55), 0 2px 8px rgba(12,46,24,0.15)",
              }}
              data-testid="returns-lab-output"
            >
              {/* BASE CASE ROW */}
              <ScenarioRow
                label="Base case"
                sub={model.base.label}
                amount={baseValue}
                unit={model.unit}
                color="#FFFFFF"
                progress={baseW}
                reduced={reduced}
              />
              {/* divider */}
              <div
                className="h-px w-full"
                style={{ background: "rgba(255,255,255,0.08)" }}
              />
              {/* STRONG CASE ROW */}
              <ScenarioRow
                label="Strong case"
                sub={model.strong.label}
                amount={strongValue}
                unit={model.unit}
                color={AMBER}
                progress={strongW}
                reduced={reduced}
              />
            </motion.div>
          </div>

          {/* HURDLE EXPLAINER */}
          <button
            type="button"
            onClick={() => setExpandedHurdle((v) => !v)}
            data-testid="returns-lab-hurdle-toggle"
            className="mt-9 w-full text-left rounded-2xl p-4 lg:p-5 transition-colors hover:bg-[#0c2e18]/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#124725]"
            style={{ background: "rgba(18,71,37,0.04)", border: "1px solid rgba(18,71,37,0.08)" }}
            aria-expanded={expandedHurdle}
          >
            <div className="flex items-center gap-3">
              <Info className="w-4 h-4 shrink-0" style={{ color: DEEP }} />
              <span
                className="text-sm font-semibold"
                style={{ color: INK, fontFamily: "var(--font-body)" }}
              >
                Why these numbers are honest.
              </span>
              <span
                className="ml-auto text-xs font-mono"
                style={{ color: MUTED }}
              >
                {expandedHurdle ? "−" : "+"}
              </span>
            </div>
            {expandedHurdle && (
              <p
                className="mt-3 text-sm leading-relaxed"
                style={{ color: MUTED, fontFamily: "var(--font-body)" }}
              >
                SparkSeed runs a <strong style={{ color: INK }}>12% investor hurdle</strong>{" "}
                — meaning we earn our profit share only after you&apos;ve
                received your capital back plus 12% a year. If the fund
                underperforms 12% CAGR, our carry is zero. These projections
                show gross multiples; net returns to you are after a 2% annual
                management fee and the 20% performance fee that only applies
                above the hurdle.
              </p>
            )}
          </button>
        </div>

        {/* DISCLAIMER */}
        <p
          className="mt-8 text-xs leading-relaxed max-w-3xl"
          style={{ color: MUTED, fontFamily: "var(--font-body)" }}
          data-testid="returns-lab-disclaimer"
        >
          Illustrative targets, not guaranteed returns. SparkSeed Ventures is a
          SEBI-registered Alternative Investment Fund and cannot promise
          performance. Investments in unlisted securities carry the risk of
          capital loss. Past performance is not indicative of future results.
          Figures shown reflect base and strong scenarios for the Indian
          early-stage market and are not an offer to invest.
        </p>

        {/* CTA */}
        <div className="mt-7">
          <button
            type="button"
            onClick={onOpenInvest}
            data-testid="returns-lab-cta"
            className="rl-cta inline-flex items-center gap-2 px-7 py-3 rounded-full font-semibold text-sm text-white transition-all"
            style={{ background: DEEP }}
          >
            Request the full PPM
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Slider + CTA styling */}
      <style>{`
        .rl-slider {
          -webkit-appearance: none;
          appearance: none;
          width: 100%;
          height: 8px;
          border-radius: 999px;
          background: linear-gradient(90deg, ${LIME} 0%, ${AMBER} var(--fill), rgba(18,71,37,0.10) var(--fill), rgba(18,71,37,0.10) 100%);
          outline: none;
        }
        .rl-slider:focus-visible {
          box-shadow: 0 0 0 4px rgba(18,71,37,0.18);
        }
        .rl-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: ${DEEP};
          border: 3px solid #fff;
          box-shadow: 0 2px 6px rgba(12,46,24,0.35);
          cursor: pointer;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .rl-slider:active::-webkit-slider-thumb,
        .rl-slider:focus-visible::-webkit-slider-thumb {
          transform: scale(1.12);
          box-shadow: 0 4px 14px rgba(12,46,24,0.45);
        }
        .rl-slider::-moz-range-thumb {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: ${DEEP};
          border: 3px solid #fff;
          box-shadow: 0 2px 6px rgba(12,46,24,0.35);
          cursor: pointer;
          transition: transform 0.15s ease;
        }
        .rl-slider:active::-moz-range-thumb,
        .rl-slider:focus-visible::-moz-range-thumb {
          transform: scale(1.12);
        }
        .rl-cta {
          box-shadow: 0 2px 8px rgba(18,71,37,0.25);
        }
        .rl-cta:hover {
          box-shadow: 0 6px 24px rgba(232,163,23,0.35), 0 2px 8px rgba(18,71,37,0.25);
          transform: translateY(-1px);
        }
        .rl-cta:focus-visible {
          outline: 2px solid ${AMBER};
          outline-offset: 3px;
        }
        @media (prefers-reduced-motion: reduce) {
          .rl-cta { transition: none; }
          .rl-cta:hover { transform: none; }
        }
      `}</style>
    </section>
  );
}

function ModelToggle({ modelKey, onChange, reduced }) {
  return (
    <div
      role="radiogroup"
      aria-label="Choose investment model"
      className="inline-flex items-center p-1 rounded-full"
      style={{ background: "rgba(18,71,37,0.07)" }}
      data-testid="returns-lab-toggle"
    >
      {Object.values(MODELS).map((m) => {
        const selected = m.key === modelKey;
        return (
          <button
            key={m.key}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(m.key)}
            data-testid={`returns-lab-toggle-${m.key}`}
            className="relative px-4 py-2 rounded-full text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#e8a317]"
            style={{
              color: selected ? "#fff" : MUTED,
              fontFamily: "var(--font-body)",
            }}
          >
            {selected && (
              <motion.span
                layoutId="rl-toggle-bg"
                transition={
                  reduced
                    ? { duration: 0 }
                    : { type: "spring", stiffness: 380, damping: 30 }
                }
                className="absolute inset-0 rounded-full"
                style={{ background: DEEP }}
              />
            )}
            <span className="relative z-10">{m.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function ScenarioRow({ label, sub, amount, unit, color, progress, reduced }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col">
          <span
            className="text-sm font-semibold"
            style={{ color: "#fff", fontFamily: "var(--font-body)" }}
          >
            {label}
          </span>
          <span
            className="text-xs mt-1"
            style={{
              color: LIME,
              fontFamily: "var(--font-mono)",
            }}
          >
            {sub}
          </span>
        </div>
        <CountAmount value={amount} unit={unit} reduced={reduced} color={color} />
      </div>
      {/* Progress bar */}
      <div
        className="h-1.5 w-full rounded-full overflow-hidden"
        style={{ background: "rgba(255,255,255,0.10)" }}
      >
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={
            reduced ? { duration: 0 } : { duration: 0.6, ease: "easeOut" }
          }
          className="h-full rounded-full"
          style={{
            background: `linear-gradient(90deg, ${LIME}, ${AMBER})`,
          }}
        />
      </div>
    </div>
  );
}
